import { ArrowUpRight, Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";

const typen = [
  {
    nr: "1.1",
    title: "Neubau",
    text: "Degressive AfA mit 5 % p. a. (§7 Abs. 5a EStG) sowie Sonder-AfA nach §7b EStG — bis zu 16 % zusätzlich über vier Jahre für neu geschaffenen Wohnraum.",
  },
  {
    nr: "1.2",
    title: "Denkmal- & Sanierungsobjekte",
    text: "Sonderabschreibungen nach §7i und §7h EStG: Bis zu 100 % der Sanierungskosten über bis zu 12 Jahre steuerlich absetzbar — der stärkste Abschreibungshebel im Immobilienbereich.",
  },
  {
    nr: "1.3",
    title: "Bestandsobjekte",
    text: "Bewährte Substanz in etablierten Lagen: planbare lineare AfA, sofortige Mieteinnahmen und ein solider Einstieg in die steueroptimierte Kapitalanlage.",
  },
];

const fokus = ["Degressive AfA & §7b EStG", "Sonder-AfA §7i / §7h", "Gestaltungsmodelle"];
const nutzen = ["Steuerersparnis ab dem ersten Jahr", "Werthaltige Sachwerte mit Substanz", "Krisenresistenz & Inflationsschutz"];

export const ChapterImmobilien = () => (
  <section id="immobilien" data-testid="chapter-real-estate" className="bg-white py-24 lg:py-36">
    <div className="mx-auto grid max-w-7xl grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 px-4 sm:px-8 lg:px-16">
      <div className="lg:col-span-4">
        <div className="lg:sticky lg:top-28">
          <Reveal>
            <p className="eyebrow mb-4">Kapitel 01</p>
            <p className="font-display text-[7rem] leading-none font-extralight text-petrol/10 select-none" aria-hidden="true">01</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-petrol -mt-10">
              Immobilien zur Kapitalanlage
            </h2>
            <p className="mt-6 text-base leading-relaxed text-slate-600">
              Die Immobilie überlebt Krisen, Inflationen und Währungsreformen.
              Das Prinzip ist denkbar einfach: Sie erwerben mit Fremdkapital
              einen Sachwert — und führen Zins und Tilgung aus Steuervorteilen
              und Mieteinnahmen zurück.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative mt-10">
              <div className="absolute -bottom-3 -right-3 h-full w-full border border-gold/50" aria-hidden="true" />
              <img
                src="https://images.unsplash.com/photo-1756669568362-40549f707007?fm=jpg&q=85&auto=format&fit=crop"
                alt="Historische Fachwerkfassade — Denkmalobjekt"
                className="relative aspect-[4/3] w-full object-cover"
                data-testid="immobilien-image"
              />
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <blockquote className="mt-10 border-l-2 border-gold pl-5" data-testid="immobilien-zitat">
              <p className="font-display text-lg italic leading-snug text-petrol">„Die einzige Möglichkeit, Vermögen aufzubauen, ist die Verschuldung in Sachwerten."</p>
              <cite className="mt-2 block font-mono text-[10px] uppercase not-italic tracking-[0.2em] text-slate-500">Freiherr von Bethmann · Bankier</cite>
            </blockquote>
          </Reveal>
        </div>
      </div>

      <div className="lg:col-span-8">
        {typen.map((t, i) => (
          <Reveal key={t.nr} delay={i * 0.1}>
            <article className="group grid grid-cols-[auto_1fr] gap-6 border-b border-petrol/10 py-8 first:pt-0 transition-colors duration-300 hover:bg-mist/60" data-testid={`immobilien-typ-${i}`}>
              <span className="font-mono text-sm text-gold pt-1">{t.nr}</span>
              <div>
                <h3 className="font-display text-xl sm:text-2xl font-medium text-petrol">{t.title}</h3>
                <p className="mt-3 text-sm sm:text-base leading-relaxed text-slate-600">{t.text}</p>
              </div>
            </article>
          </Reveal>
        ))}

        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap gap-3" data-testid="immobilien-fokus">
            {fokus.map((f) => (
              <span key={f} className="border border-forest/25 bg-sage/40 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-forest">
                {f}
              </span>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-10 border-l-2 border-gold bg-mist p-8" data-testid="immobilien-nutzen">
            <p className="eyebrow mb-5">Ihr Nutzen</p>
            <ul className="space-y-3">
              {nutzen.map((n) => (
                <li key={n} className="flex items-start gap-3 text-sm sm:text-base text-slate-700">
                  <Check size={16} className="mt-1 shrink-0 text-forest" />
                  {n}
                </li>
              ))}
            </ul>
            <button data-testid="immobilien-cta-check" onClick={() => scrollToId("#check")} className="btn-primary group mt-8">
              Immobilien-Check anfordern
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
